import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, VpnKey } from '@mui/icons-material';
import { login, forgotPassword, resetPassword } from '../../Services/AuthServices.js/Auth';

// Importing Assets
import LoginImg from '../../Assets/LoginImg.png';
import Logo from '../../Assets/logo.png';
import TransparentLogo from '../../Assets/transparentlogo.png';
import EmailIcon from '../../Assets/EmailLock.png';
import PasswordIcon from '../../Assets/PasswordLock.png';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();

  // Mode state: 'login' | 'forgot_email' | 'reset_password'
  const [viewMode, setViewMode] = useState('login');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  // Submit Login
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
      onLogin();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1: Send OTP to email
  const handleSendOtpSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address.');
      return;
    }
    resetMessages();
    setIsLoading(true);
    try {
      const res = await forgotPassword(formData.email);
      if (res && res.success) {
        setResetToken(res.resetToken);
        setSuccess(res.message || 'OTP sent to your email successfully');
        setViewMode('reset_password');
      } else {
        setError(res?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please check your email and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Reset password using OTP & new password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!formData.otp) {
      setError('Please enter the OTP.');
      return;
    }
    if (!formData.newPassword) {
      setError('Please enter a new password.');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New password and confirm password do not match.');
      return;
    }
    resetMessages();
    setIsLoading(true);
    try {
      const res = await resetPassword(resetToken, formData.otp, formData.newPassword);
      if (res && res.success) {
        setSuccess(res.message || 'Password reset successfully. You can now login with your new password.');
        setFormData(prev => ({
          ...prev,
          password: '',
          otp: '',
          newPassword: '',
          confirmPassword: ''
        }));
        setResetToken('');
        setViewMode('login');
      } else {
        setError(res?.message || 'Failed to reset password. Please check your OTP.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed. Invalid or expired OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP handler
  const handleResendOtp = async () => {
    resetMessages();
    setIsLoading(true);
    try {
      const res = await forgotPassword(formData.email);
      if (res && res.success) {
        setResetToken(res.resetToken);
        setSuccess('OTP has been resent to your email.');
      } else {
        setError(res?.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: '56px',
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#e0e0e0' },
      '&.Mui-focused fieldset': { borderColor: '#333', borderWidth: '1px' },
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100vh',
      width: '100%',
      backgroundColor: '#fff',
      fontFamily: '"Inter", sans-serif',
      overflow: 'hidden'
    }}>
      {/* Dynamic Font Import */}
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');`}
      </style>

      {/* Left Side: Background Image */}
      <Box
        sx={{
          flex: 0.9,
          position: 'relative',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${LoginImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }
        }}
      />

      {/* Right Side: Auth Form */}
      <Box sx={{
        flex: { xs: 1, md: 0.4 },
        width: { xs: '100%', md: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: { xs: '32px 24px', sm: '40px' },
        position: 'relative',
        backgroundColor: '#fff'
      }}>
        {/* VIEW MODE 1: LOGIN */}
        {viewMode === 'login' && (
          <Box
            component="form"
            onSubmit={handleLoginSubmit}
            autoComplete="off"
            sx={{
              width: '100%',
              maxWidth: '380px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            {/* Header Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 4, md: 6 }, width: '100%' }}>
              <Box component="img" src={Logo} alt="Heal at India" sx={{ height: 48, width: 'auto' }} />
            </Box>

            {success && (
              <Box sx={{
                color: '#2e7d32',
                fontSize: '13px',
                backgroundColor: '#edf7ed',
                borderRadius: '6px',
                padding: '12px',
                width: '100%',
                mb: 2,
                borderLeft: '4px solid #2e7d32',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                {success}
              </Box>
            )}

            <TextField
              fullWidth
              name="email"
              placeholder="Email ID"
              variant="outlined"
              margin="normal"
              value={formData.email}
              onChange={handleChange}
              autoFocus
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <Box component="img" src={EmailIcon} sx={{ width: 18, mr: 1, opacity: 0.8 }} alt="email" />
                  </InputAdornment>
                ),
              }}
              sx={inputStyle}
            />

            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              variant="outlined"
              margin="normal"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <Box component="img" src={PasswordIcon} sx={{ width: 18, mr: 1, opacity: 0.8 }} alt="password" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ mr: 0.5, opacity: 0.7 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={inputStyle}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
              <Typography
                variant="body2"
                onClick={() => {
                  setViewMode('forgot_email');
                  resetMessages();
                }}
                sx={{
                  color: '#000',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: '14px',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Forgot Password?
              </Typography>
            </Box>

            {error && (
              <Box sx={{
                color: '#d32f2f',
                fontSize: '13px',
                backgroundColor: '#fff5f5',
                borderRadius: '6px',
                padding: '12px',
                width: '100%',
                mt: 2,
                borderLeft: '4px solid #d32f2f',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                {error}
              </Box>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                bgcolor: '#000',
                color: '#fff',
                height: '48px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 500,
                boxShadow: 'none',
                '&.Mui-disabled': { bgcolor: '#eaeaea' }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
          </Box>
        )}

        {/* VIEW MODE 2: FORGOT PASSWORD - STEP 1 (EMAIL) */}
        {viewMode === 'forgot_email' && (
          <Box
            component="form"
            onSubmit={handleSendOtpSubmit}
            autoComplete="off"
            sx={{
              width: '100%',
              maxWidth: '380px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            {/* Header Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 }, width: '100%' }}>
              <Box component="img" src={Logo} alt="Heal at India" sx={{ height: 48, width: 'auto' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, color: '#111', mb: 1, textAlign: 'center' }}>
              Forgot Password?
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 3, textAlign: 'center', fontSize: '14px' }}>
              Step 1 of 2: Enter your email address to receive an OTP.
            </Typography>

            <TextField
              fullWidth
              name="email"
              placeholder="Email ID"
              variant="outlined"
              margin="normal"
              value={formData.email}
              onChange={handleChange}
              autoFocus
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <Box component="img" src={EmailIcon} sx={{ width: 18, mr: 1, opacity: 0.8 }} alt="email" />
                  </InputAdornment>
                ),
              }}
              sx={inputStyle}
            />

            {error && (
              <Box sx={{
                color: '#d32f2f',
                fontSize: '13px',
                backgroundColor: '#fff5f5',
                borderRadius: '6px',
                padding: '12px',
                width: '100%',
                mt: 2,
                borderLeft: '4px solid #d32f2f',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                {error}
              </Box>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                bgcolor: '#000',
                color: '#fff',
                height: '48px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 500,
                boxShadow: 'none',
                '&.Mui-disabled': { bgcolor: '#eaeaea' }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>

            <Button
              startIcon={<ArrowBack />}
              onClick={() => {
                setViewMode('login');
                resetMessages();
              }}
              sx={{
                mt: 2,
                textTransform: 'none',
                color: '#666',
                fontSize: '14px',
                '&:hover': { color: '#000', backgroundColor: 'transparent' }
              }}
            >
              Back to Login
            </Button>
          </Box>
        )}

        {/* VIEW MODE 3: FORGOT PASSWORD - STEP 2 (OTP & RESET PASSWORD) */}
        {viewMode === 'reset_password' && (
          <Box
            component="form"
            onSubmit={handleResetPasswordSubmit}
            autoComplete="off"
            sx={{
              width: '100%',
              maxWidth: '380px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            {/* Header Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 4 }, width: '100%' }}>
              <Box component="img" src={Logo} alt="Heal at India" sx={{ height: 48, width: 'auto' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, color: '#111', mb: 1, textAlign: 'center' }}>
              Reset Password
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2, textAlign: 'center', fontSize: '14px' }}>
              Step 2 of 2: Enter the 6-digit OTP sent to <strong>{formData.email}</strong>
            </Typography>

            {success && (
              <Box sx={{
                color: '#2e7d32',
                fontSize: '13px',
                backgroundColor: '#edf7ed',
                borderRadius: '6px',
                padding: '12px',
                width: '100%',
                mb: 1,
                borderLeft: '4px solid #2e7d32',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                {success}
              </Box>
            )}

            <TextField
              fullWidth
              name="otp"
              placeholder="Enter 6-Digit OTP"
              variant="outlined"
              margin="normal"
              value={formData.otp}
              onChange={handleChange}
              autoFocus
              inputProps={{ maxLength: 6 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <VpnKey sx={{ width: 20, color: '#666', mr: 1, opacity: 0.8 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputStyle}
            />

            <TextField
              fullWidth
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              variant="outlined"
              margin="normal"
              value={formData.newPassword}
              onChange={handleChange}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <Box component="img" src={PasswordIcon} sx={{ width: 18, mr: 1, opacity: 0.8 }} alt="password" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                      sx={{ mr: 0.5, opacity: 0.7 }}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={inputStyle}
            />

            <TextField
              fullWidth
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm New Password"
              variant="outlined"
              margin="normal"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <Box component="img" src={PasswordIcon} sx={{ width: 18, mr: 1, opacity: 0.8 }} alt="password" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ mr: 0.5, opacity: 0.7 }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={inputStyle}
            />

            {error && (
              <Box sx={{
                color: '#d32f2f',
                fontSize: '13px',
                backgroundColor: '#fff5f5',
                borderRadius: '6px',
                padding: '12px',
                width: '100%',
                mt: 1,
                borderLeft: '4px solid #d32f2f',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                {error}
              </Box>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 3,
                bgcolor: '#000',
                color: '#fff',
                height: '48px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 500,
                boxShadow: 'none',
                '&.Mui-disabled': { bgcolor: '#eaeaea' }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
              <Typography
                variant="body2"
                onClick={() => {
                  setViewMode('forgot_email');
                  resetMessages();
                }}
                sx={{
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '13px',
                  '&:hover': { color: '#000', textDecoration: 'underline' }
                }}
              >
                Change Email
              </Typography>
              <Typography
                variant="body2"
                onClick={handleResendOtp}
                sx={{
                  color: '#000',
                  fontWeight: 500,
                  cursor: isLoading ? 'default' : 'pointer',
                  fontSize: '13px',
                  opacity: isLoading ? 0.5 : 1,
                  '&:hover': { textDecoration: isLoading ? 'none' : 'underline' }
                }}
              >
                Resend OTP
              </Typography>
            </Box>

            <Button
              startIcon={<ArrowBack />}
              onClick={() => {
                setViewMode('login');
                resetMessages();
              }}
              sx={{
                mt: 2,
                textTransform: 'none',
                color: '#666',
                fontSize: '14px',
                '&:hover': { color: '#000', backgroundColor: 'transparent' }
              }}
            >
              Back to Login
            </Button>
          </Box>
        )}

        {/* Faint Watermark Logo */}
        <Box
          component="img"
          src={TransparentLogo}
          sx={{
            position: 'absolute',
            bottom: { xs: '-40px', md: '-60px' },
            right: { xs: '-40px', md: '-60px' },
            width: { xs: '320px', md: '200px' },
            height: 'auto',
            opacity: { xs: 0.08, md: 0.25 },
            pointerEvents: 'none',
            zIndex: 1,
            filter: 'grayscale(1)'
          }}
          alt=""
        />
      </Box>
    </Box>
  );
};

export default Login;
